import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, vShow as _vShow, withDirectives as _withDirectives, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-0a9f20fc"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "fleet-map-content" }
const _hoisted_2 = { class: "fleet-pane-toolbar" }
const _hoisted_3 = {
  key: 1,
  class: "fleet-state-tip fleet-state-tip--static"
}

import { ref, watch, nextTick, computed, reactive } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import CmTable from '@/components/cmTable/index.vue'
import { TableOpt, TableActOpt, PageOption } from '@/components/cmTable/types'
import { FormSize } from '@/components/cmForm/types'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import GroupTreeSelector from '@/views/dashboard/_components/group-tree-selector/group-tree-selector.vue'
import { formatTimeStamp } from '@/utils/time'
import { readCssVar } from '@/utils/chart-theme-colors'
import {
  GoogleMapsInfoWindow,
  GoogleMapsMap,
  GoogleMapsMarker,
  GoogleMapsSdk,
  isGoogleMapsKeyConfigured,
  loadGoogleMaps
} from './google-maps'
import { useLocationSourceLabels } from './location-source'
import { buildMarkerIcon, escapeXml, formatPointTime, loadLocationSvgText } from './marker-icon'
import { LocationInfoService, MapList, MatListRequestParams } from './service'

const PAGE_LIMIT = 500
const MAX_PAGES = 50
const DEFAULT_MAP_ZOOM = 12
/** 无数据时地图默认中心（东京） */
const DEFAULT_MAP_CENTER_LAT = 35.681236
const DEFAULT_MAP_CENTER_LNG = 139.767125
/** 离线终端图钉色（SVG data URI 内无 CSS 变量，取运行时实际值） */

export default /*#__PURE__*/_defineComponent({
  __name: 'fleet-map',
  props: {
  modelValue: {
    type: Boolean,
    default: false
  },
  /** 默认分组（打开时对齐列表当前分组，null=全部） */
  defaultGroupId: {
    type: [Number, null],
    default: -1
  }
},
  emits: ['update:modelValue'],
  setup(__props, { emit: __emit }) {

const OFFLINE_PIN_FILL = readCssVar('--el-color-info', '#909399')

const props = __props
const emit = __emit
const { t, locale } = useI18n()
const injector = useReflectiveInjector([LocationInfoService])
const service = injector.get(LocationInfoService)
const SOURCE_LABELS = useLocationSourceLabels()

const isKeyConfigured = isGoogleMapsKeyConfigured()
const isFullscreen = ref(false)
const isLoading = ref(false)
const loadFailed = ref(false)
const noData = ref(false)
/** 当前分组（组件内自持；每次打开从 defaultGroupId 重新同步） */
const currentGroupId = ref<number | null>(null)
/** 分组树 remount 键：每次打开 +1，让树按新默认分组重新初始化 */
const treeKey = ref(0)
const total = ref(0)
const mapContainer = ref<HTMLElement | null>(null)
const markers = ref<MapList[]>([])
/** 抽屉内 tab：map=终端位置（默认） / list=终端位置列表 */
const activeTab = ref('map')
/** 列表行“在地图上显示”聚焦的终端 id，null=展示当前分组全部终端 */
const focusDeviceId = ref<string | null>(null)
/** 列表 tab 的 cmTable 实例与查询参数（page/limit 由 cmTable 内部管理，limit 供滚动加载每页条数） */
const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const listParams = reactive<{ page: number; limit: number,group_id:string | number }>({ page: 1, limit: 20, group_id:currentGroupId.value  })
const pageOption: PageOption = { pageNumName: 'page', pageSizeName: 'limit' }

const title = computed(() => {
  void locale.value
  const base = t('groupClient.terminalLocation')
  // 聚焦单终端时按地图实际绘制数计数，其余用服务端总数
  const count = focusDeviceId.value ? markers.value.length : total.value
  const suffix = count ? `[${count}${t('groupClient.terminalsUnit')}]` : ''
  return base + suffix
})
let sdk: GoogleMapsSdk | null = null
let mapInstance: GoogleMapsMap | null = null
let mapMarkers: GoogleMapsMarker[] = []
let infoWindows: GoogleMapsInfoWindow[] = []

watch(() => props.modelValue, (value) => {
  if (!value) {
    return
  }
  // 打开：默认分组对齐列表当前分组；treeKey 强制分组树重新初始化
  currentGroupId.value = props.defaultGroupId
  focusDeviceId.value = null
  activeTab.value = 'map'
  treeKey.value += 1
  nextTick(() => {
    resizeMap()
    void redraw()
  })
})

watch(isFullscreen, () => {
  // 抽屉宽度变化后地图容器尺寸改变，等布局完成再重算
  nextTick(() => resizeMap())
})

function toggleFullscreen() {
  isFullscreen.value = !isFullscreen.value
}

function handleClose() {
  isFullscreen.value = false
  emit('update:modelValue', false)
}

function resizeMap() {
  if (sdk?.event && mapInstance) {
    sdk.event.trigger(mapInstance, 'resize')
  }
}

function handleGroupSelected(groupId: number) {
  /** 树挂载时默认 emit 一次，相同 id 短路避免重复请求 */
  if (currentGroupId.value === groupId) {
    return
  }
  currentGroupId.value = groupId
  // 分组变了，单终端聚焦失效，回到全量展示
  focusDeviceId.value = null
  void redraw()
  // 分组筛选两个 tab 公用：列表 tab 已挂载且当前可见时同步刷新（切到列表 tab 时另有 tab-click 刷新兜底）
  if (activeTab.value === 'list') {
    void refreshList()
  }
}

/** 切回地图 tab 通知 SDK 重算尺寸；切入列表 tab 刷新列表（分组可能在 map tab 被改过）；
 *  老版本 element-plus 的 tab-click 首参是 pane 实例，paneName 为 ComputedRef，须取 .value 比较 */
function handleTabClick(pane: { paneName?: { value?: string } }) {
  if (pane?.paneName?.value === 'map') {
    nextTick(() => resizeMap())
  } else if (pane?.paneName?.value === 'list') {
    void refreshList()
  }
}

/** 列表行“在地图上显示”：切回地图 tab 并只绘制该终端 */
function handleListAction({ type, rowItem }: { type: string; rowItem?: MapList }) {
  if (type !== 'showOnMap' || !rowItem?.device_id) {
    return
  }
  focusDeviceId.value = rowItem.device_id
  activeTab.value = 'map'
  void redraw()
  nextTick(() => resizeMap())
}

/** 退出单终端聚焦，恢复当前分组全量绘制 */
function handleResetFocus() {
  focusDeviceId.value = null
  void redraw()
}

function ensureMapInstance() {
  if (mapInstance || !mapContainer.value || !sdk) {
    return
  }
  mapInstance = new sdk.Map(mapContainer.value, {
    zoom: DEFAULT_MAP_ZOOM,
    center: new sdk.LatLng(DEFAULT_MAP_CENTER_LAT, DEFAULT_MAP_CENTER_LNG),
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false
  })
}

/** 分页拉取全部终端位置：每页 500，收集满 total 或本页不足即停，50 页硬上限；按 device_id 去重防分页边界重复 */
async function fetchAll(): Promise<MapList[]> {
  const collected: MapList[] = []
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const res = await service.getLocationMapList({
      page,
      limit: PAGE_LIMIT,
      ...(currentGroupId.value != null ? { group_id: String(currentGroupId.value) } : {})
    })
    const list = res.list ?? []
    collected.push(...list)
    total.value = res.total ?? collected.length
    if (list.length < PAGE_LIMIT || collected.length >= total.value) {
      break
    }
  }
  return Array.from(new Map(collected.map(item => [item.device_id, item])).values())
}

async function redraw() {
  if (!isKeyConfigured) {
    return
  }
  isLoading.value = true
  loadFailed.value = false
  try {
    const [mapsSdk, pinSvgText, list] = await Promise.all([
      loadGoogleMaps(),
      loadLocationSvgText(),
      fetchAll()
    ])
    sdk = mapsSdk
    ensureMapInstance()
    // “在地图上显示”聚焦时只绘制该终端
    const drawList = focusDeviceId.value
      ? list.filter(item => item.device_id === focusDeviceId.value)
      : list
    drawMarkers(drawList, pinSvgText)
    // 抽屉展开动画后容器尺寸稳定，补一次 resize
    setTimeout(() => resizeMap(), 300)
  } catch (error) {
    loadFailed.value = true
  } finally {
    isLoading.value = false
  }
}

function clearDrawings() {
  mapMarkers.forEach(marker => marker.setMap(null))
  infoWindows.forEach(infoWindow => infoWindow.close())
  mapMarkers = []
  infoWindows = []
}

/** 每台终端一个标点：三行卡片（计算机名/用户名/时间），在线主题色钉、离线灰钉 */
function drawMarkers(list: MapList[], pinSvgText: string) {
  const maps = sdk
  const map = mapInstance
  if (!maps || !map) {
    return
  }
  clearDrawings()

  const valid = list.filter(item => item.longitude && item.latitude)
  markers.value = valid
  noData.value = valid.length === 0
  if (noData.value) {
    return
  }

  const bounds = new maps.LatLngBounds()
  valid.forEach(item => {
    const position = new maps.LatLng(Number(item.latitude), Number(item.longitude))
    const online = item.login_status === 1
    const icon = buildMarkerIcon(maps, pinSvgText, {
      lines: [
        { text: item.computer_name, bold: true },
        ...(item.user_mark ? [{ text: item.user_mark, kind: 'secondary' as const }] : []),
        { text: formatPointTime(item.location_time), kind: 'tertiary' }
      ],
      pinFill: online ? undefined : OFFLINE_PIN_FILL
    })
    const marker = new maps.Marker({ position, map, icon, optimized: false, title: item.computer_name })
    bindInfoWindow(maps, map, marker, item)
    mapMarkers.push(marker)
    bounds.extend(position)
  })
  if (valid.length === 1) {
    const item = valid[0]
    map.setCenter(new maps.LatLng(Number(item.latitude), Number(item.longitude)))
    map.setZoom(DEFAULT_MAP_ZOOM)
    return
  }
  map.fitBounds(bounds)
}

/** 点击标点展示完整终端信息（用户数据必须 escapeXml，InfoWindow content 是 HTML） */
function bindInfoWindow(maps: GoogleMapsSdk, map: GoogleMapsMap, marker: GoogleMapsMarker, item: MapList) {
  const infoWindow = new maps.InfoWindow({
    content: [
      escapeXml(item.computer_name),
      `${t('groupClient.registerUserName')}: ${escapeXml(item.user_mark || '--')}`,
      `${t('groupClient.groupName')}: ${escapeXml(item.group_name || '--')}`,
      `${t('groupClient.locationInfoColTime')}: ${formatTimeStamp(item.location_time) || '--'}`,
      `${t('groupClient.locationInfoColSource')}: ${SOURCE_LABELS.value[item.source] ?? '--'}`,
      `${t('groupClient.locationInfoColIp')}: ${escapeXml(item.client_ip || '--')}`,
      `${t('groupClient.locationInfoColAccuracy')}: ${item.accuracy > 0
        ? t('groupClient.locationInfoAccuracyValue', { value: item.accuracy })
        : t('groupClient.locationInfoAccuracyUnknown')}`,
      t(item.login_status === 1 ? 'groupClient.online' : 'groupClient.offline')
    ].join('<br/>')
  })
  marker.addListener('click', () => infoWindow.open(map, marker))
  infoWindows.push(infoWindow)
}

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    path: 'list',
    totalPath: 'total',
    rowKey: 'device_id',
    height: '100%',
    bodyStyle: 'box-sizing:border-box;margin-top:15px;',
    containerOpt: { style: { flex: '1', minHeight: 0, overflow: 'hidden',} },
    showActionBar: false,
    showForm: false,
    showSearch: false,
    showRightToolbar: false,
    showTip: false,
    selection: false,
    usePagination: false,
    columns: [
      { prop: 'computer_name', label: t('groupClient.computerName'), minWidth: 150, formatter: formatEmpty, showOverflow: true },
      { prop: 'user_mark', label: t('groupClient.registerUserName'), minWidth: 130, formatter: formatEmpty, showOverflow: true },
      { prop: 'group_name', label: t('groupClient.groupName'), minWidth: 140, formatter: formatEmpty, showOverflow: true },
      { prop: 'location_time', label: t('groupClient.locationInfoColTime'), minWidth: 160, formatter: formatLocationTime },
      { prop: 'source', label: t('groupClient.locationInfoColSource'), minWidth: 110, formatter: formatSource },
      { prop: 'login_status', label: t('groupClient.terminalStatus'), minWidth: 100, formatter: formatLoginStatus },
      { prop: 'client_ip', label: t('groupClient.locationInfoColIp'), minWidth: 140, formatter: formatEmpty },
      { prop: 'accuracy', label: t('groupClient.locationInfoColAccuracy'), minWidth: 110, formatter: formatAccuracy }
    ]
  }
})

/** 操作列：在地图上显示该终端（无经纬度时禁用） */
const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: { label: t('groupClient.operation'), minWidth: 130, align: 'left', fixed: 'right' },
    actionButtons: (row?: MapList) => [
      {
        type: 'showOnMap',
        name: t('groupClient.fleetMapShowOnMap'),
        opt: { size: FormSize.Small, text: true, type: 'text' },
        disabled: () => !row?.longitude || !row?.latitude
      }
    ]
  }
})

/** 列表 tab 数据源：与地图同一接口，按当前分组过滤 */
function listApi(params: Record<string, unknown>) {
  return service.getLocationMapList({
    ...params,
    ...(currentGroupId.value != null ? { group_id: String(currentGroupId.value) } : {})
  } as MatListRequestParams).then(res => ({ ...res, list: res.list ?? [] }))
}

function formatEmpty(_row: unknown, value: string): string {
  return value || '--'
}

function formatLocationTime(_row: unknown, value: string) {
  return formatTimeStamp(value) || '--'
}

function formatSource(_row: unknown, value: number) {
  return SOURCE_LABELS.value[value] ?? '--'
}

function formatLoginStatus(_row: unknown, value: number) {
  return t(value === 1 ? 'groupClient.online' : 'groupClient.offline')
}

function formatAccuracy(_row: unknown, value: number) {
  return value > 0
    ? t('groupClient.locationInfoAccuracyValue', { value })
    : t('groupClient.locationInfoAccuracyUnknown')
}

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_tab_pane = _resolveComponent("el-tab-pane")!
  const _component_el_tabs = _resolveComponent("el-tabs")!
  const _component_el_drawer = _resolveComponent("el-drawer")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createBlock(_component_el_drawer, {
    "append-to-body": "",
    "custom-class": "fleet-map-wrap",
    title: title.value,
    "model-value": __props.modelValue,
    "show-close": "",
    size: isFullscreen.value ? 'calc(100% - 10px)' : '80%',
    onClose: handleClose
  }, {
    default: _withCtx(() => [
      _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          (focusDeviceId.value)
            ? (_openBlock(), _createBlock(_component_el_button, {
                key: 0,
                size: "mini",
                type: "text",
                class: "focus-reset-btn",
                onClick: handleResetFocus
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('groupClient.fleetMapBackToAll')), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (_openBlock(), _createBlock(GroupTreeSelector, {
            key: treeKey.value,
            referenceWidth: "auto",
            "default-group-id": currentGroupId.value,
            onSelected: handleGroupSelected
          }, null, 8, ["default-group-id"])),
          _createVNode(_component_el_button, {
            circle: "",
            size: "mini",
            style: {"margin-left":"auto","padding":"8px"},
            onClick: toggleFullscreen
          }, {
            default: _withCtx(() => [
              _createElementVNode("i", {
                class: _normalizeClass(isFullscreen.value ? 'el-icon-close' : 'el-icon-full-screen'),
                "aria-hidden": "true"
              }, null, 2)
            ]),
            _: 1
          })
        ]),
        _createVNode(_component_el_tabs, {
          modelValue: activeTab.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((activeTab).value = $event)),
          class: "fleet-tabs",
          onTabClick: handleTabClick
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_tab_pane, {
              label: _ctx.$t('groupClient.terminalLocation'),
              name: "map"
            }, {
              default: _withCtx(() => [
                (_unref(isKeyConfigured))
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createElementVNode("div", {
                        ref_key: "mapContainer",
                        ref: mapContainer,
                        class: "fleet-map-container el-card"
                      }, null, 512),
                      _withDirectives(_createElementVNode("div", { class: "fleet-state-tip" }, _toDisplayString(_ctx.$t('groupClient.locationInfoMapLoadFailed')), 513), [
                        [_vShow, loadFailed.value]
                      ]),
                      _withDirectives(_createElementVNode("div", { class: "fleet-state-tip" }, _toDisplayString(_ctx.$t('groupClient.locationInfoEmptyMap')), 513), [
                        [_vShow, !loadFailed.value && noData.value]
                      ])
                    ], 64))
                  : (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_ctx.$t('groupClient.locationInfoMapKeyMissing')), 1))
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_component_el_tab_pane, {
              label: _ctx.$t('groupClient.fleetMapTabList'),
              name: "list",
              lazy: ""
            }, {
              default: _withCtx(() => [
                _createVNode(CmTable, {
                  ref_key: "tableRef",
                  ref: tableRef,
                  modelValue: listParams,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((listParams) = $event)),
                  "table-opt": tableOpt.value,
                  "table-act-opt": tableActOpt.value,
                  api: listApi,
                  "page-option": pageOption,
                  "first-loading": true,
                  onHandleAction: handleListAction
                }, null, 8, ["modelValue", "table-opt", "table-act-opt"])
              ]),
              _: 1
            }, 8, ["label"])
          ]),
          _: 1
        }, 8, ["modelValue"])
      ])), [
        [_directive_loading, isLoading.value]
      ])
    ]),
    _: 1
  }, 8, ["title", "model-value", "size"]))
}
}

})